import { enumMembers, type ApiSchema } from "../apiSchema";
import { readStringList } from "../content/contentDecoders";
import { CHECK_STATUSES, type CheckStatus } from "../content/reviewDecoders";
import { readReleaseChecks, type ReleaseChecks } from "./releaseCheckDecoders";
import {
  readArray,
  readBoolean,
  readInteger,
  readNullableString,
  readObject,
  readOneOf,
  readString,
  type JsonObject,
} from "../http/jsonDecoding";

/**
 * Knowledge data as the server sends it (`server/src/dmw/api/routes/data/bodies.py`,
 * `docs/components/knowledge-data.md`): extraction results with every recorded fact of
 * their provenance, datasets and their immutable versions.
 */

export type CurationState = ApiSchema<"CurationState">;
export type CurationDecisionKind = ApiSchema<"CurationDecisionKind">;
export type PackageFormat = ApiSchema<"PackageFormat">;
export type GenerationKind = ExtractionSummary["generation"];

export const CURATION_STATES = enumMembers<CurationState>({ proposed: true, accepted: true, rejected: true, superseded: true, withdrawn: true });
export const PACKAGE_FORMATS = enumMembers<PackageFormat>({ nquads: true, trig: true });
const DECISION_KINDS = enumMembers<CurationDecisionKind>({ accept: true, reject: true, supersede: true, withdraw: true });
const GENERATION_KINDS: readonly GenerationKind[] = ["agent_turn", "human_review", "carried_forward"];
const RESULT_KINDS: readonly ExtractionSummary["kind"][] = ["annotations", "instance_data"];
const ROUTE_KINDS: readonly RecordedRoute["kind"][] = ["agent", "api_key"];

/** Where a result is decided: its acceptance step, a Thread step (ADR-0026 slice 3a). */
export interface AcceptanceStep {
  readonly threadId: string;
  readonly stepKey: string;
}

export interface ExtractionSummary {
  /** The Thread entry that holds the output: `<thread>#<n>`. */
  readonly source: string;
  readonly iri: string;
  readonly state: CurationState;
  readonly kind: "annotations" | "instance_data";
  readonly document: string;
  /** `null` for instance data, which is written against the project's model instead. */
  readonly labelList: string | null;
  readonly statements: number;
  readonly generation: "agent_turn" | "human_review" | "carried_forward";
  readonly recordedAt: string;
  /** Its acceptance step, once one was asked for it; `null` before. */
  readonly review: AcceptanceStep | null;
}

/** One span of a source that says a statement of instance data. */
export interface InstanceEvidence {
  readonly segment: string;
  readonly segmentIri: string;
  readonly start: number;
  readonly end: number;
  readonly quote: string;
}

/** One statement of instance data: its triples (N-Triples with the individuals' stable IRIs) and evidence. */
export interface InstanceStatement {
  readonly number: number;
  readonly iri: string;
  readonly note: string;
  readonly triples: readonly string[];
  readonly evidence: readonly InstanceEvidence[];
}

export interface Statement {
  readonly number: number;
  readonly iri: string;
  readonly segment: string;
  readonly segmentIri: string;
  readonly start: number;
  readonly end: number;
  readonly quote: string;
  readonly label: string;
  readonly labelIri: string;
  readonly ontologyTerms: readonly string[];
  readonly note: string;
}

export interface RecordedRoute {
  readonly kind: "agent" | "api_key";
  readonly name: string;
  readonly requestedModel: string | null;
}

export interface AgentGeneration {
  readonly kind: "agent_turn";
  readonly turnId: string;
  readonly requestEntry: number;
  readonly resultEntry: number;
  readonly stepKey: string | null;
  readonly workflow: string | null;
  readonly step: string | null;
  readonly prompt: string | null;
  readonly contentVersion: string;
  readonly corpusVersion: string | null;
  readonly referencesRead: readonly string[];
  readonly inputDigest: string;
  readonly route: RecordedRoute;
  readonly ran: { readonly name: string; readonly version: string | null; readonly model: string | null } | null;
  readonly host: string;
  readonly connectorId: string | null;
  readonly requestedBy: string | null;
  readonly startedAt: string | null;
  readonly finishedAt: string | null;
  readonly serverVersion: string;
}

export interface CarriedForwardGeneration {
  readonly kind: "carried_forward";
  readonly revises: string;
  readonly changeSetId: string;
  readonly impact: "unchanged" | "moved";
  readonly curator: string;
  readonly at: string;
  readonly note: string;
}

export interface HumanGeneration {
  readonly kind: "human_review";
  readonly stepRequestEntry: number;
  readonly stepKey: string;
  readonly step: string;
  readonly reviewer: string;
  /** `<thread>#<n>` of the output the participant reviewed. */
  readonly reviewedOutput: string | null;
}

export interface CurationDecision {
  readonly supersededBy: string | null;
  readonly decisionId: string;
  readonly kind: CurationDecisionKind;
  readonly curator: string;
  readonly comment: string;
  readonly at: string;
}

export interface PendingProposal {
  readonly source: string;
  readonly by: string;
  readonly recordedAt: string;
  readonly newer: boolean;
}

export interface Extraction {
  readonly lineage: { readonly acceptedHead: string | null; readonly pending: readonly PendingProposal[] };
  readonly workingStateOf: string | null;
  readonly superseded: readonly string[];
  readonly skipped: readonly string[];
  readonly source: string;
  readonly iri: string;
  readonly provenanceGraph: string;
  readonly state: CurationState;
  readonly recordedAt: string;
  readonly projectId: string;
  readonly threadId: string;
  readonly outputEntry: number;
  readonly document: string;
  readonly labelList: string | null;
  readonly generation: AgentGeneration | HumanGeneration | CarriedForwardGeneration;
  readonly statements: readonly Statement[];
  /** Empty unless the result is instance data. */
  readonly instanceStatements: readonly InstanceStatement[];
  /** The model version instance data was written against (`<collection>@<commit>`); `null` for annotations. */
  readonly model: string | null;
  readonly decisions: readonly CurationDecision[];
  readonly mappingVersion: string;
  readonly assertionDigest: string;
  readonly provenanceDigest: string;
  readonly canDecide: boolean;
  /** Its acceptance step, once one was asked for it; `null` before. */
  readonly review: AcceptanceStep | null;
}

export interface DatasetMetadata {
  readonly title: string;
  readonly description: string;
  readonly licence: string;
  readonly keywords: readonly string[];
}

export interface DatasetVersionSummary {
  readonly number: number;
  readonly iri: string;
  readonly createdAt: string;
  readonly createdBy: string;
  readonly members: number;
  readonly packageDigest: string;
  readonly packageQuads: number;
  /** The release checks' status (D4); `null` for a version made before them: it cannot be exported. */
  readonly checksStatus: CheckStatus | null;
  readonly checkedAgainst: string | null;
}

export interface Dataset {
  readonly datasetId: string;
  readonly iri: string;
  readonly projectId: string;
  readonly metadata: DatasetMetadata;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly latestVersion: number;
  readonly versions: readonly DatasetVersionSummary[];
}

export interface DatasetVersion {
  readonly iri: string;
  readonly datasetId: string;
  readonly number: number;
  readonly metadata: DatasetMetadata;
  readonly members: readonly string[];
  readonly mappingVersion: string;
  /** The core ontology the package conforms to; a mapping may name none (F-5). */
  readonly coreOntology: { readonly versionIri: string; readonly commit: string } | null;
  readonly iriBase: string;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly packageDigest: string;
  readonly packageQuads: number;
  /** The release's check report (D4); `null` for a version made before the release checks. */
  readonly checks: ReleaseChecks | null;
}

export function readExtractionSummary(value: unknown): ExtractionSummary {
  const body = readObject(value, "extraction summary");
  return {
    source: readString(body, "source"),
    iri: readString(body, "iri"),
    state: readOneOf(body, "state", CURATION_STATES),
    kind: readOneOf(body, "kind", RESULT_KINDS),
    document: readString(body, "document"),
    labelList: readNullableString(body, "label_list"),
    statements: readInteger(body, "statements"),
    generation: readOneOf(body, "generation", GENERATION_KINDS),
    recordedAt: readString(body, "recorded_at"),
    review: readOptionalAcceptanceStep(body),
  };
}

export function readAcceptanceStep(value: unknown): AcceptanceStep {
  const body = readObject(value, "acceptance step");
  return { threadId: readString(body, "thread_id"), stepKey: readString(body, "step_key") };
}

function readOptionalAcceptanceStep(body: JsonObject): AcceptanceStep | null {
  const review = body["review"];
  return review === null || review === undefined ? null : readAcceptanceStep(review);
}

export function readExtraction(value: unknown): Extraction {
  const body = readObject(value, "extraction");
  const facts = readObject(body["facts"], "facts");
  const lineage = readObject(body["lineage"], "lineage");
  return {
    source: readString(body, "source"),
    iri: readString(body, "iri"),
    lineage: {
      acceptedHead: readNullableString(lineage, "accepted_head"),
      pending: readArray(lineage["pending"], "pending", (value) => {
        const proposal = readObject(value, "proposal");
        return { source: readString(proposal, "source"), by: readString(proposal, "by"), recordedAt: readString(proposal, "recorded_at"), newer: readBoolean(proposal, "newer") };
      }),
    },
    workingStateOf: readNullableString(body, "working_state_of"),
    superseded: readStringList(body, "superseded"),
    skipped: readStringList(body, "skipped"),
    provenanceGraph: readString(body, "provenance_graph"),
    state: readOneOf(body, "state", CURATION_STATES),
    recordedAt: readString(body, "recorded_at"),
    projectId: readString(facts, "project_id"),
    threadId: readString(facts, "thread_id"),
    outputEntry: readInteger(facts, "output_entry"),
    document: readString(facts, "document"),
    labelList: readNullableString(facts, "label_list"),
    generation: readGeneration(readObject(facts["generation"], "generation")),
    statements: readArray(body["statements"], "statements", readStatement),
    instanceStatements: readArray(body["instance_statements"], "instance_statements", readInstanceStatement),
    model: facts["instance_data"] === null || facts["instance_data"] === undefined ? null : readString(readObject(facts["instance_data"], "instance data"), "model"),
    decisions: readArray(body["decisions"], "decisions", readDecision),
    mappingVersion: readString(body, "mapping_version"),
    assertionDigest: readString(body, "assertion_digest"),
    provenanceDigest: readString(body, "provenance_digest"),
    canDecide: readBoolean(body, "can_decide"),
    review: readOptionalAcceptanceStep(body),
  };
}

export function readInstanceStatement(value: unknown): InstanceStatement {
  const body = readObject(value, "instance statement");
  return {
    number: readInteger(body, "number"),
    iri: readString(body, "iri"),
    note: readString(body, "note"),
    triples: readStringList(body, "triples"),
    evidence: readArray(body["evidence"], "evidence", readEvidence),
  };
}

function readEvidence(value: unknown): InstanceEvidence {
  const body = readObject(value, "evidence");
  return {
    segment: readString(body, "segment"),
    segmentIri: readString(body, "segment_iri"),
    start: readInteger(body, "start"),
    end: readInteger(body, "end"),
    quote: readString(body, "quote"),
  };
}

function readStatement(value: unknown): Statement {
  const body = readObject(value, "statement");
  return {
    number: readInteger(body, "number"),
    iri: readString(body, "iri"),
    segment: readString(body, "segment"),
    segmentIri: readString(body, "segment_iri"),
    start: readInteger(body, "start"),
    end: readInteger(body, "end"),
    quote: readString(body, "quote"),
    label: readString(body, "label"),
    labelIri: readString(body, "label_iri"),
    ontologyTerms: readStringList(body, "ontology_terms"),
    note: readString(body, "note"),
  };
}

function readGeneration(body: JsonObject): AgentGeneration | HumanGeneration | CarriedForwardGeneration {
  const kind = readOneOf(body, "kind", GENERATION_KINDS);
  if (kind === "carried_forward") {
    return { kind, revises: readString(body, "revises"), changeSetId: readString(body, "change_set_id"),
      impact: readOneOf(body, "impact", ["unchanged", "moved"] as const),
      curator: readString(body, "curator"), at: readString(body, "at"), note: readString(body, "note") };
  }
  if (kind === "human_review") {
    return {
      kind: "human_review",
      stepRequestEntry: readInteger(body, "step_request_entry"),
      stepKey: readString(body, "step_key"),
      step: readString(body, "step"),
      reviewer: readString(body, "reviewer"),
      reviewedOutput: readNullableString(body, "reviewed_output"),
    };
  }
  const route = readObject(body["route"], "route");
  const ran = body["ran"] === null ? null : readObject(body["ran"], "ran");
  return {
    kind: "agent_turn",
    turnId: readString(body, "turn_id"),
    requestEntry: readInteger(body, "request_entry"),
    resultEntry: readInteger(body, "result_entry"),
    stepKey: readNullableString(body, "step_key"),
    workflow: readNullableString(body, "workflow"),
    step: readNullableString(body, "step"),
    prompt: readNullableString(body, "prompt"),
    contentVersion: readString(body, "content_version"),
    corpusVersion: readNullableString(body, "corpus_version"),
    referencesRead: readStringList(body, "references_read"),
    inputDigest: readString(body, "input_digest"),
    route: {
      kind: readOneOf(route, "kind", ROUTE_KINDS),
      name: readString(route, "name"),
      requestedModel: readNullableString(route, "requested_model"),
    },
    ran:
      ran === null
        ? null
        : { name: readString(ran, "name"), version: readNullableString(ran, "version"), model: readNullableString(ran, "model") },
    host: readString(body, "host"),
    connectorId: readNullableString(body, "connector_id"),
    requestedBy: readNullableString(body, "requested_by"),
    startedAt: readNullableString(body, "started_at"),
    finishedAt: readNullableString(body, "finished_at"),
    serverVersion: readString(body, "server_version"),
  };
}

function readDecision(value: unknown): CurationDecision {
  const body = readObject(value, "curation decision");
  return {
    supersededBy: body["superseded_by"] === undefined ? null : readNullableString(body, "superseded_by"),
    decisionId: readString(body, "decision_id"),
    kind: readOneOf(body, "kind", DECISION_KINDS),
    curator: readString(body, "curator"),
    comment: readString(body, "comment"),
    at: readString(body, "at"),
  };
}

function readMetadata(value: unknown): DatasetMetadata {
  const body = readObject(value, "dataset metadata");
  return {
    title: readString(body, "title"),
    description: readString(body, "description"),
    licence: readString(body, "licence"),
    keywords: readStringList(body, "keywords"),
  };
}

export function readDataset(value: unknown): Dataset {
  const body = readObject(value, "dataset");
  return {
    datasetId: readString(body, "dataset_id"),
    iri: readString(body, "iri"),
    projectId: readString(body, "project_id"),
    metadata: readMetadata(body["metadata"]),
    createdBy: readString(body, "created_by"),
    createdAt: readString(body, "created_at"),
    latestVersion: readInteger(body, "latest_version"),
    versions: readArray(body["versions"], "dataset versions", readDatasetVersionSummary),
  };
}

export function readDatasetVersionSummary(value: unknown): DatasetVersionSummary {
  const version = readObject(value, "dataset version summary");
  return {
    number: readInteger(version, "number"),
    iri: readString(version, "iri"),
    createdAt: readString(version, "created_at"),
    createdBy: readString(version, "created_by"),
    members: readInteger(version, "members"),
    packageDigest: readString(version, "package_digest"),
    packageQuads: readInteger(version, "package_quads"),
    checksStatus: version["checks_status"] == null ? null : readOneOf(version, "checks_status", CHECK_STATUSES),
    checkedAgainst: readNullableString(version, "checked_against"),
  };
}

export function readDatasetVersion(value: unknown): DatasetVersion {
  const body = readObject(value, "dataset version");
  const release = readObject(body["release"], "release");
  const mapping = readObject(release["mapping"], "mapping");
  const core = mapping["core_ontology"] == null ? null : readObject(mapping["core_ontology"], "core ontology");
  return {
    iri: readString(body, "iri"),
    datasetId: readString(release, "dataset_id"),
    number: readInteger(release, "number"),
    metadata: readMetadata(release["metadata"]),
    members: readArray(release["members"], "members", (item) => readString(readObject(item, "member"), "extraction")),
    mappingVersion: readString(mapping, "mapping_version"),
    coreOntology: core === null ? null : { versionIri: readString(core, "version_iri"), commit: readString(core, "commit") },
    iriBase: readString(release, "iri_base"),
    createdBy: readString(release, "created_by"),
    createdAt: readString(release, "created_at"),
    packageDigest: readString(body, "package_digest"),
    packageQuads: readInteger(body, "package_quads"),
    checks: body["checks"] == null ? null : readReleaseChecks(body["checks"]),
  };
}
